import React, { useMemo, useState } from 'react';
import { Wand2, Loader2, ArrowRight, ShieldCheck, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('safetydesk81_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CONF_COLORS = {
  ALTA:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  MEDIA: 'bg-amber-100 text-amber-800 border-amber-200',
  BASSA: 'bg-slate-100 text-slate-700 border-slate-200',
};

const NormalizeNamesPage = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(new Set()); // ids esclusi
  const [includeMedium, setIncludeMedium] = useState(true);
  const [search, setSearch] = useState('');
  const [confFilter, setConfFilter] = useState('all'); // all | ALTA | MEDIA
  const [lastResult, setLastResult] = useState(null);

  const analyze = async () => {
    setAnalyzing(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/workers/normalize-names/preview`, {}, { headers: authHeaders() });
      setPreview(data);
      setExcluded(new Set());
      toast.success(`Analisi completata: ${data.counts?.SWAP || 0} lavoratori da correggere`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore analisi');
    } finally {
      setAnalyzing(false);
    }
  };

  const proposals = useMemo(() => preview?.proposals || [], [preview]);
  const filtered = useMemo(() => {
    let list = proposals;
    if (confFilter !== 'all') list = list.filter((p) => p.confidence === confFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        `${p.current_firstName} ${p.current_lastName} ${p.fiscalCode}`.toLowerCase().includes(q));
    }
    return list;
  }, [proposals, search, confFilter]);

  const toggleExcluded = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectableIds = useMemo(
    () => proposals.filter((p) => includeMedium || p.confidence !== 'MEDIA').map((p) => p.id),
    [proposals, includeMedium],
  );
  const willApplyCount = selectableIds.filter((id) => !excluded.has(id)).length;

  const doApply = async () => {
    if (!willApplyCount) return;
    if (!window.confirm(`Confermi lo swap firstName↔lastName su ${willApplyCount} lavoratori? Verrà creato un backup automatico rollbackabile.`)) return;
    setApplying(true);
    try {
      const ids = selectableIds.filter((id) => !excluded.has(id));
      const { data } = await axios.post(`${API}/admin/workers/normalize-names/apply`, {
        ids,
        include_medium_confidence: includeMedium,
      }, { headers: authHeaders() });
      setLastResult(data);
      toast.success(`${data.applied} lavoratori normalizzati. Backup: ${data.backup_collection}`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore applicazione');
    } finally {
      setApplying(false);
    }
  };

  const doRollback = async () => {
    if (!lastResult?.opId) return;
    if (!window.confirm(`Ripristini i valori pre-normalizzazione dal backup ${lastResult.backup_collection}?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/workers/normalize-names/rollback/${lastResult.opId}`, {}, { headers: authHeaders() });
      toast.success(`${data.restored} lavoratori ripristinati`);
      setLastResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rollback');
    }
  };

  return (
    <div className="space-y-5" data-testid="normalize-names-page">
      <div className="rounded-2xl bg-gradient-to-r from-[#023F80] to-[#022F60] text-white p-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#FFCB05]/20 flex items-center justify-center shrink-0">
          <Wand2 size={24} className="text-[#FFCB05]" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold">Normalizzatore batch nomi lavoratori</h1>
          <p className="text-white/70 text-sm mt-1 leading-relaxed">
            Rileva e corregge i lavoratori con <strong>firstName</strong> e <strong>lastName</strong> invertiti nel DB usando il <strong>Codice Fiscale italiano</strong>. Per lavoratori senza CF si usa un'euristica sui nomi italiani comuni (confidenza MEDIA). Backup automatico rollbackabile.
          </p>
        </div>
      </div>

      {!preview && !lastResult && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="max-w-md mx-auto">
            <ShieldCheck size={40} className="mx-auto text-[#023F80] mb-3" />
            <h2 className="text-lg font-bold text-gray-800">Analisi lavoratori</h2>
            <p className="text-sm text-slate-500 mt-2">
              Scansiona tutti i lavoratori attivi (esclusi gli archiviati) e propone gli swap.
              Nessuna modifica al DB in questa fase.
            </p>
            <button
              onClick={analyze}
              disabled={analyzing}
              data-testid="normalize-analyze-btn"
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#023F80] text-white text-sm font-bold hover:bg-[#022F60] disabled:opacity-60"
            >
              {analyzing ? <><Loader2 size={16} className="animate-spin" /> Analisi in corso…</> : <><Wand2 size={16} /> Analizza tutti i lavoratori</>}
            </button>
          </div>
        </div>
      )}

      {lastResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3" data-testid="normalize-result">
          <ShieldCheck size={20} className="text-emerald-700 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="text-sm font-bold text-emerald-900">Normalizzazione completata</div>
            <div className="text-xs text-emerald-800 mt-1">
              {lastResult.applied} lavoratori aggiornati · Backup: <code>{lastResult.backup_collection}</code> (opId <code>{lastResult.opId}</code>)
            </div>
          </div>
          <button
            onClick={doRollback}
            data-testid="normalize-rollback-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-xs font-bold hover:bg-emerald-100"
          >
            <RefreshCw size={12} /> ROLLBACK
          </button>
          <button
            onClick={analyze}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]"
          >
            <Wand2 size={12} /> Analizza di nuovo
          </button>
        </div>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Totale lavoratori" value={preview.total_workers} color="#023F80" bg="#e0e7ff" />
            <StatCard label="Da correggere" value={preview.counts?.SWAP || 0} color="#b91c1c" bg="#fee2e2" testId="normalize-count-swap" />
            <StatCard label="Già corretti" value={preview.counts?.OK_INVARIATO || 0} color="#15803d" bg="#dcfce7" />
            <StatCard label="Ambigui (non toccati)" value={preview.counts?.AMBIGUO || 0} color="#8a6608" bg="#fef7d4" />
            <StatCard label="Confidenza ALTA" value={preview.by_confidence?.ALTA || 0} color="#065f46" bg="#d1fae5" />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong>Confidenza ALTA</strong> = CF italiano combacia perfettamente dopo lo swap.
                <strong> MEDIA</strong> = CF assente ma euristica sui nomi italiani suggerisce l'inversione.
                Le righe <strong>AMBIGUO</strong> non vengono mai toccate.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[240px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome, cognome o codice fiscale…" className="pl-9 h-10 border-gray-200" data-testid="normalize-search" />
              </div>
              <div className="flex gap-1">
                {['all', 'ALTA', 'MEDIA'].map((k) => (
                  <button
                    key={k}
                    onClick={() => setConfFilter(k)}
                    data-testid={`normalize-conf-${k}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${confFilter === k ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  >
                    {k === 'all' ? 'Tutte' : k}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                <input type="checkbox" checked={includeMedium} onChange={(e) => setIncludeMedium(e.target.checked)} data-testid="normalize-include-medium" />
                Includi confidenza MEDIA (euristica)
              </label>
            </div>

            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="bg-gray-100 rounded-l-xl px-3 py-3 w-10 text-center">✓</th>
                    <th className="text-left bg-gray-100 px-3 py-3">Codice fiscale</th>
                    <th className="text-left bg-gray-100 px-3 py-3">Attuale (errato)</th>
                    <th className="text-left bg-gray-100 px-3 py-3 w-8"></th>
                    <th className="text-left bg-gray-100 px-3 py-3">Proposto (corretto)</th>
                    <th className="text-left bg-gray-100 px-3 py-3">Confidenza</th>
                    <th className="text-left bg-gray-100 rounded-r-xl px-3 py-3">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const isExcluded = excluded.has(p.id);
                    const isDisabled = !includeMedium && p.confidence === 'MEDIA';
                    return (
                      <tr
                        key={p.id}
                        className={`border-b border-gray-50 ${isExcluded || isDisabled ? 'opacity-40 bg-slate-50' : 'hover:bg-gray-50/60'}`}
                        data-testid={`normalize-row-${p.id}`}
                      >
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!isExcluded && !isDisabled}
                            disabled={isDisabled}
                            onChange={() => toggleExcluded(p.id)}
                            data-testid={`normalize-check-${p.id}`}
                          />
                        </td>
                        <td className="px-3 py-3 font-mono text-[11px] text-slate-600">{p.fiscalCode || '—'}</td>
                        <td className="px-3 py-3">
                          <span className="text-red-700 font-semibold">{p.current_firstName}</span>
                          <span className="text-slate-400"> / </span>
                          <span className="text-red-700 font-semibold">{p.current_lastName}</span>
                        </td>
                        <td className="px-3 py-3 text-slate-400"><ArrowRight size={14} /></td>
                        <td className="px-3 py-3">
                          <span className="text-emerald-700 font-semibold">{p.proposed_firstName}</span>
                          <span className="text-slate-400"> / </span>
                          <span className="text-emerald-700 font-semibold">{p.proposed_lastName}</span>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${CONF_COLORS[p.confidence] || CONF_COLORS.BASSA}`}>
                            {p.confidence}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-[11px] text-slate-500 max-w-lg">
                          <span className="line-clamp-2" title={p.reason}>{p.reason}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="py-10 text-center text-sm text-slate-400">Nessuna proposta con i filtri correnti</div>
              )}
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 flex-wrap border-t border-slate-100 pt-4">
              <div className="text-xs text-slate-500">
                Verranno applicati <strong className="text-slate-800">{willApplyCount}</strong> swap · {excluded.size} esclusi manualmente
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setPreview(null); setExcluded(new Set()); }}
                  className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
                  data-testid="normalize-cancel-btn"
                >
                  Annulla
                </button>
                <button
                  onClick={doApply}
                  disabled={applying || willApplyCount === 0}
                  data-testid="normalize-apply-btn"
                  className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2"
                >
                  {applying ? <><Loader2 size={14} className="animate-spin" /> Applico…</> : <><Wand2 size={14} /> Applica {willApplyCount} swap</>}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, bg, testId }) => (
  <div className="rounded-2xl p-4" style={{ background: bg }} data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</div>
    <div className="mt-1 text-3xl font-extrabold" style={{ color }}>{value ?? 0}</div>
  </div>
);

export default NormalizeNamesPage;
